import type { FC } from "react";

import Button from "@/design-system/button";
import { languageSelector } from "@/src/contexts";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type QueryXsMax } from "../../styles/tokens.stylex";
import ResourcesEn from "./Breadcrumb.Resources.En.json";
import Resources from "./Breadcrumb.Resources.json";

import * as cssPortal from "../shared/Portal.module.css";

export interface IBreadcrumb {
    list: IBreadcrumbItem[];
    isNewGrid: boolean;
}

export interface IBreadcrumbItem {
    label?: string;
    labelKey?: keyof typeof Resources;
    path?: string;
}

export const Breadcrumb: FC<IBreadcrumb> = ({ list, isNewGrid }) => {
    const resources = languageSelector(Resources, ResourcesEn);

    const navCss = `${stylex.props(styles.nav).className} ${isNewGrid ? cssPortal.setSectionColOne : ""}`;

    return (
        <div {...stylex.props(styles.comp)}>
            <div className={isNewGrid ? cssPortal.standardContainerV2 : cssPortal.standardContainer}>
                <nav className={navCss}>
                    <ol {...stylex.props(styles.ol)} itemScope itemType="http://schema.org/BreadcrumbList">
                        {[{ label: "Goodie", path: "/" }, ...list].map((item, index) => {
                            index += 1;
                            const text = item.labelKey ? resources[item.labelKey] : item.label;

                            if (index <= list.length) {
                                return (
                                    <li
                                        {...stylex.props(styles.li)}
                                        key={item.path}
                                        itemProp="itemListElement"
                                        itemScope
                                        itemType="http://schema.org/ListItem"
                                    >
                                        <Button
                                            as="a"
                                            size="None"
                                            href={process.env.Base_URL + item.path}
                                            styleX={styles.link}
                                            itemProp="item"
                                        >
                                            <span itemProp="name">{text}</span>
                                        </Button>
                                        <meta itemProp="position" content={`${index}`} />
                                        <svg
                                            {...stylex.props(styles.arrow)}
                                            viewBox="0 0 6 10"
                                            fill="currentColor"
                                        >
                                            <path d="M5.63,4.56,1.25.18a.63.63,0,0,0-.88,0,.63.63,0,0,0,0,.88L4.31,5,.37,8.94a.62.62,0,0,0,.88.88L5.63,5.44A.63.63,0,0,0,5.63,4.56Z" />
                                        </svg>
                                    </li>
                                );
                            }
                            return (
                                <li
                                    {...stylex.props(styles.li)}
                                    itemProp="itemListElement"
                                    itemScope
                                    itemType="http://schema.org/ListItem"
                                    key={index}
                                >
                                    <span itemProp="name">{text}</span>
                                    <meta itemProp="position" content={`${index}`} />
                                </li>
                            );
                        })}
                    </ol>
                </nav>
            </div>
        </div>
    );
};

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    comp: {
        backgroundColor: "white",
        color: "#c2c2c2",
    },
    nav: {
        position: "relative",
        height: { default: "41px", [xsMax]: "34px" },
    },
    ol: {
        listStyle: "none",
        paddingBlock: "12px",
        paddingInline: "0px",
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
        lineHeight: 0,
        whiteSpace: "nowrap",
        marginTop: 0,
        overflowX: "hidden",
        "::before": {
            backgroundImage: "linear-gradient(90deg, hsla(0, 0%, 100%, 0), #fff 70%)",
            content: "",

            position: "absolute",
            height: "100%",
            right: 0,
            top: 0,
            width: "30px",
        },
    },
    li: {
        fontSize: {
            default: "0.875rem",
            [xsMax]: "0.75rem",
        },
    },
    arrow: {
        color: "#c2c2c2",
        height: "10px",
        marginBottom: 0,
        marginLeft: ComposableSpacing.s2,
        marginRight: ComposableSpacing.s2,
        marginTop: 0,
        width: "6px",
    },
    link: {
        padding: 0,
        textDecoration: "none",
        color: "#c2c2c2",
        fontSize: "inherit",
    },
});
